import { AdminFeatureInline } from '@website/components/admin/AdminFeatureInline';
import useCreateConversationAndRedirect from '@website/components/chat/utils/useCreateConversationAndRedirect';
import PriceFormat from '@website/components/utilities/PriceFormat';
import Truncate from '@website/components/utilities/Truncate';
import { HostDashboardGroupBookingsQuery } from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useMemo } from 'react';
import { Badge, Button, Table } from 'react-bootstrap';
import { MdMessage } from 'react-icons/md';
import { SpaceWithOwner } from './GroupBookingsList';
import HostAddGuestNote from './HostAddGuestNote';

export function getBalanceOutstandingForHosts(booking: {
  spaces: HostDashboardGroupBookingsQuery['group']['bookings']['items'][number]['spaces'];
}): number {
  return booking.spaces.reduce((balanceOutstandingAccumulator, space) => {
    const newBalanceOutstandingAccumulator =
      balanceOutstandingAccumulator + space.guestBalance.amountZD;
    return newBalanceOutstandingAccumulator;
  }, 0);
}

type GuestsRowProps = {
  space: SpaceWithOwner;
  host: HostDashboardGroupBookingsQuery['group']['trip']['host'];
  showEmails: boolean;
};

/**
 * Guest Row
 */
export function GuestsRow({ space, host, showEmails }: GuestsRowProps) {
  const guestName = space.guest.givenName
    ? `${space.guest.givenName} ${space.guest.familyName}`
    : undefined;
  const ownerName = `${space.owner.givenName} ${space.owner.familyName}`;
  const phone: string = space.guest.phone || space.owner.phone || '';
  const email: string = space.guest.email || space.owner.email;
  const ownerID = space.owner.id;
  const { userID } = space.guest;

  const isBalanceDue = useMemo(() => {
    const balanceDue = getBalanceOutstandingForHosts({
      spaces: [space],
    });
    return balanceDue > 0;
  }, [space]);

  const startConversation = useCreateConversationAndRedirect(
    host.id,
    userID ?? ownerID,
    host.urlSlug,
  );

  const rowSpan = space.guest.hostNotes ? 2 : 1;

  return (
    <>
      <tr>
        <td rowSpan={rowSpan}>
          <div className="d-block">
            {guestName || <i>Booked by {ownerName}</i>}
            {' ('}{' '}
            <AdminFeatureInline>
              <Link
                target="_blank"
                href={`/me/${userID ?? space.owner.id}`}
                passHref
              >
                Profile
              </Link>
              {' | '}
              <Link
                target="_blank"
                passHref
                href={`/me/${space.owner.id}/bookings/${space.booking.id}`}
              >
                Booking
              </Link>
              {' | '}
            </AdminFeatureInline>
            <HostAddGuestNote
              spaceID={space.id}
              note={space.guest?.hostNotes}
              variant="link"
            />
            {' )'}
          </div>
          <div className="d-block text-muted text-sm">
            Booked on{' '}
            {DateTime.fromISO(space.created).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </div>
        </td>
        <td>
          <a href={`tel:${phone}`}>{phone}</a>
        </td>
        {showEmails && (
          <td>
            <a href={`mailto:${email}`}>{email}</a>
          </td>
        )}
        <td>
          <Button
            type="button"
            onClick={() => startConversation()}
            variant="primary"
          >
            <MdMessage size={16} />
            <span className="px-2">Send Message</span>
          </Button>
        </td>
        <td>
          <PriceFormat
            amount={space.hostTotal.amountZD}
            currencyIso={space.hostTotal.currency}
          />
        </td>
        <td>
          <Badge
            pill
            bg={!isBalanceDue ? 'success' : 'warning'}
            className="mb-3"
          >
            {!isBalanceDue ? 'Fully Paid' : 'Balance Due'}
          </Badge>
        </td>
        <td>
          <Badge
            pill
            bg={space.guest.completed ? 'success' : 'danger'}
            className="mb-3"
          >
            {space.guest.completed ? 'Complete' : ' Incomplete'}
          </Badge>
        </td>
      </tr>

      {space.guest?.hostNotes && (
        <tr>
          <td colSpan={6} className="text-sm text-muted p-3">
            <Truncate
              lines={2}
              enableExpand
              enableCollapse
              className="text-muted text-sm"
            >
              {space.guest?.hostNotes ?? 'No notes'}
            </Truncate>
          </td>
        </tr>
      )}
    </>
  );
}

type GuestsTableProps = {
  spaces: SpaceWithOwner[];
  host: HostDashboardGroupBookingsQuery['group']['trip']['host'];
  showEmails: boolean;
};

/**
 * Guests Table
 *
 * The main table that displays the guest names and contact details.
 */
export default function GuestsTable({
  spaces,
  host,
  showEmails,
}: GuestsTableProps) {
  return (
    <Table
      striped
      bordered
      responsive
      style={{
        minWidth: '1000px',
      }}
    >
      <thead>
        <tr>
          <th>Name</th>
          <th>Phone</th>
          {showEmails && <th>Email</th>}
          <th>Message booking owner</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Trip Form</th>
        </tr>
      </thead>
      <tbody>
        {spaces.map((space) => (
          <GuestsRow
            space={space}
            key={space.id}
            host={host}
            showEmails={showEmails}
          />
        ))}
      </tbody>
    </Table>
  );
}
